import { MouseEvent, useCallback, useContext, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { Prompt, useHistory } from "react-router";

import ActionIconButton from "@components/buttons/ActionIconButton";
import BackButton from "@components/buttons/BackButton";
import TertiaryButton from "@components/buttons/TertiaryButton";
import DeleteConfirmationAlert from "@components/DeleteConfirmationAlert";
import EditModeBanner from "@components/EditModeBanner";
import BaseErrors from "@components/fields/BaseErrors";
import ToggleCheckbox from "@components/fields/ToggleCheckbox";
import WorksheetInput from "@components/fieldsNative/WorksheetInput";
import Form from "@components/forms/Form";
import styles from "@components/forms/WorksheetEditor.module.css";
import Tag from "@components/labels/Tag";
import Icon from "@components/text/Icon";
import BodyCopy from "@components/typography/BodyCopy";
import Heading2 from "@components/typography/Heading2";
import { openerContext } from "@context/Contexts";
import useLazyQueryWithPromise from "@hooks/useLazyQueryWithPromise";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import useMyExpert from "@hooks/useMyExpert";
import useSanitizeHtml from "@hooks/useSanitizeHtml";
import useUnsavedChanges from "@hooks/useUnsavedChanges";
import { OpenerTypeEnum } from "@typing/Enums";
import {
  GetPdfDownloadDocument,
  GetPdfDownloadQuery,
  GetPdfDownloadQueryVariables,
  PdfTypeEnum,
  WorksheetFragment,
  WorksheetTemplateFragment,
  WorksheetUpdateDocument,
  WorksheetUpdateMutation,
  WorksheetUpdateMutationVariables
} from "@typing/Generated";
import { downloadFile } from "@utils/downloadUtils";
import { ErrorSetter } from "@utils/formUtils";
import { sortByPosition, tagPropsForFocusArea } from "@utils/modelUtils";
import { isMobileWeb, isNativeApp } from "@utils/platformUtils";
import { journeyRoute } from "@utils/routeUtils";

export type WorksheetModification = {
  completed: boolean | null;
  details: string | null;
  item: WorksheetTemplateFragment["worksheetTemplateItems"][0] | WorksheetFragment["worksheetItems"][0];
};

export type WorksheetEditorFormData = {
  modifications: WorksheetModification[];
};

type Props = {
  handleWorksheetEdit?: () => void;
  initialWorksheet: WorksheetTemplateFragment | WorksheetFragment;
  journeyId: string;
  loading: boolean;
  onSave: (
    modifications: WorksheetModification[],
    setBaseErrors: (errors: string[]) => void,
    setError: ErrorSetter<WorksheetEditorFormData>,
    leavingLocation?: string | undefined,
    resetForm?: () => void
  ) => Promise<void>;
};

const WorksheetEditor = ({ handleWorksheetEdit, initialWorksheet, journeyId, loading, onSave }: Props) => {
  const history = useHistory();
  const intl = useIntl();
  const myExpert = useMyExpert();
  const [submitting, setSubmitting] = useState(false);

  const defaultValues: WorksheetEditorFormData = useMemo(() => {
    if (initialWorksheet.__typename === "WorksheetTemplate") {
      return {
        modifications: initialWorksheet.worksheetTemplateItems.map(item => ({
          completed: false,
          details: null,
          item
        }))
      };
    }
    return {
      modifications: initialWorksheet.worksheetItems.map(item => ({
        completed: item.completed,
        details: item.details,
        item
      }))
    };
  }, [initialWorksheet]);

  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const form = useForm<WorksheetEditorFormData>({
    defaultValues,
    mode: "onChange"
  });

  const modifications = form.watch("modifications");
  const { toggleOpener } = useContext(openerContext);

  const getPdfDownload = useLazyQueryWithPromise<GetPdfDownloadQuery, GetPdfDownloadQueryVariables>(
    GetPdfDownloadDocument
  );

  const [worksheetUpdate] = useMutationWithValidation<WorksheetUpdateMutation, WorksheetUpdateMutationVariables>(
    WorksheetUpdateDocument,
    "worksheet"
  );

  const handleDownload = useCallback(
    (event: MouseEvent<HTMLIonButtonElement>, completed: boolean) => {
      event.preventDefault();
      if (completed) {
        getPdfDownload({
          fetchPolicy: "no-cache",
          variables: { id: initialWorksheet.id, journeyId, type: PdfTypeEnum.worksheet_completed }
        }).then(response => {
          downloadFile(response.data.pdfDownload.url, "grayce-worksheet-completed.pdf");
        });
      } else {
        getPdfDownload({
          fetchPolicy: "no-cache",
          variables: { id: initialWorksheet.id, journeyId, type: PdfTypeEnum.worksheet }
        }).then(response => {
          downloadFile(response.data.pdfDownload.url, "grayce-worksheet-blank.pdf");
        });
      }
    },
    [getPdfDownload, initialWorksheet.id, journeyId]
  );

  const handleCompletedChange = (completed: boolean) => {
    setSubmitting(true);
    worksheetUpdate({
      completed,
      id: initialWorksheet.id
    }).then(() => {
      setSubmitting(false);
    });
  };

  const changes = useMemo(() => {
    if (initialWorksheet.__typename === "WorksheetTemplate") {
      return modifications.filter(modification => modification.completed || modification.details);
    }

    const arr: WorksheetModification[] = [];
    modifications.forEach(modification => {
      const worksheetItem = initialWorksheet.worksheetItems.find(item => item.id === modification.item.id);
      if (modification.completed !== worksheetItem?.completed || modification.details !== worksheetItem.details) {
        arr.push(modification);
      }
    });
    return arr;
  }, [initialWorksheet, modifications]);

  const unsavedChanges = useUnsavedChanges({ hasUnsavedChanges: changes.length > 0 });

  const onSubmit = useCallback(
    async (leavingLocation?: string) => {
      const changesTemp = [...changes];
      unsavedChanges.opener.close();
      setBaseErrors([]);

      return onSave(changesTemp, setBaseErrors, form.setError, leavingLocation, form.reset);
    },
    [changes, form, unsavedChanges.opener, onSave]
  );

  const handleDiscard = () => {
    form.reset();
  };

  const handleOpenMessages = useCallback(() => {
    const isMobile = isMobileWeb() || isNativeApp();

    if (initialWorksheet.__typename === "Worksheet") {
      toggleOpener(OpenerTypeEnum.MESSAGES, !isMobile, initialWorksheet);
    }

    if (isMobile) {
      history.push(
        journeyRoute({
          journeyId,
          linkAfterJourney: `/messages`,
          myExpert
        })
      );
    }
  }, [history, initialWorksheet, journeyId, myExpert, toggleOpener]);

  const handleItemCompletedChange = (newWorksheetItem: WorksheetModification) => {
    form.setValue(
      "modifications",
      modifications.map(modification => {
        if (modification.item.id === newWorksheetItem.item.id) {
          return {
            ...newWorksheetItem
          };
        }
        return { ...modification };
      })
    );
  };

  const sanitizedDescription = useSanitizeHtml(initialWorksheet.description, journeyId, initialWorksheet);

  const worksheetItems: WorksheetFragment["worksheetItems"] | WorksheetTemplateFragment["worksheetTemplateItems"] =
    initialWorksheet.__typename === "Worksheet"
      ? sortByPosition(initialWorksheet.worksheetItems)
      : sortByPosition(initialWorksheet.worksheetTemplateItems);

  const hasDuplicateGoalInEditMode =
    initialWorksheet.__typename === "Worksheet" && initialWorksheet.goal?.hasDuplicateGoalInEditMode;

  return (
    <div>
      <Prompt {...unsavedChanges.promptProps} />
      <Form hookForm={form} onSubmit={form.handleSubmit(() => onSubmit())}>
        {loading && (
          <div className="align-center">
            <Icon icon="spinner" size="xlarge" />
          </div>
        )}
        {hasDuplicateGoalInEditMode && (
          <div className="ion-margin-bottom">
            <EditModeBanner />
          </div>
        )}
        <div className={loading ? "ion-hide" : ""}>
          <BackButton />

          <div className={styles.grid}>
            <div className={styles.worksheetEdit}>
              <div className={styles.tags}>
                {(initialWorksheet.__typename === "Worksheet"
                  ? initialWorksheet.worksheetTemplate.focusAreas
                  : initialWorksheet.focusAreas
                ).map(focusArea => (
                  <Tag key={focusArea} {...tagPropsForFocusArea(focusArea, intl)} />
                ))}
              </div>
              <div className={styles.titleAndEdit}>
                <Heading2>{initialWorksheet.title}</Heading2>
                {initialWorksheet.__typename === "Worksheet" && (
                  <ActionIconButton
                    ariaLabelKey="models.worksheet.edit"
                    disabled={hasDuplicateGoalInEditMode}
                    icon="edit"
                    onClick={handleWorksheetEdit}
                    size="medium"
                  />
                )}
              </div>
              <div className={styles.descriptionSpacing}>
                <BodyCopy color="gray-500">{sanitizedDescription}</BodyCopy>
              </div>

              {worksheetItems.map((item, index) => {
                const modificationItem = modifications.find(
                  change => change.item.id === item.id
                ) as WorksheetModification;
                return (
                  <div key={modificationItem.item.id}>
                    {modificationItem.item.acceptsCheckbox ? (
                      <div className={styles.itemWrapper}>
                        <div
                          className={modificationItem.item.acceptsDetails ? styles.toggleWithDetails : styles.toggle}
                        >
                          <div className={styles.checkbox}>
                            <ToggleCheckbox
                              checked={modificationItem.completed ?? false}
                              disabled={hasDuplicateGoalInEditMode}
                              hoverContent={
                                hasDuplicateGoalInEditMode
                                  ? intl.formatMessage({ id: "pages.documents.worksheets.cannotUpdateDuplicateGoal" })
                                  : undefined
                              }
                              name="itemCheckbox"
                              onChange={checked =>
                                handleItemCompletedChange({ ...modificationItem, completed: checked })
                              }
                              tabIndex={0}
                            />
                          </div>
                          <div className={styles.title}>{modificationItem.item.title}</div>
                        </div>
                        {modificationItem.item.acceptsDetails && (
                          <div className={styles.details}>
                            <WorksheetInput
                              disabled={hasDuplicateGoalInEditMode}
                              name="employer"
                              onChange={e =>
                                handleItemCompletedChange({ ...modificationItem, details: e.target.value ?? "" })
                              }
                              value={modificationItem.details ?? ""}
                            />
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className={`${styles.subHeader} ${index === 0 ? styles.noDivider : ""}`}>{item.title}</div>
                    )}
                  </div>
                );
              })}

              {initialWorksheet.__typename === "Worksheet" && (
                <div className={styles.footerContent}>
                  <div className={styles.footer}>
                    <ToggleCheckbox
                      checked={initialWorksheet.completed}
                      disabled={submitting || hasDuplicateGoalInEditMode}
                      hoverContent={
                        hasDuplicateGoalInEditMode
                          ? intl.formatMessage({ id: "pages.documents.worksheets.cannotUpdateDuplicateGoal" })
                          : undefined
                      }
                      name="worksheetComplete"
                      onChange={handleCompletedChange}
                      tabIndex={0}
                    />
                    <BodyCopy>
                      <FormattedMessage
                        id={`models.worksheet.${initialWorksheet.completed ? "markAsIncomplete" : "markAsComplete"}`}
                        values={{ expertFirstName: initialWorksheet.journey.primaryExpert?.firstName }}
                      />
                    </BodyCopy>
                  </div>
                </div>
              )}
            </div>

            <div className={styles.toolbar}>
              {changes && initialWorksheet.__typename === "WorksheetTemplate" && (
                <TertiaryButton
                  className="ion-margin-end"
                  icon="trash"
                  large
                  onClick={handleDiscard}
                  textKey="dictionary.clear"
                />
              )}
              <div>
                <TertiaryButton
                  disabled={
                    form.formState.isSubmitting ||
                    !form.formState.isValid ||
                    !!(
                      initialWorksheet.__typename === "Worksheet" &&
                      changes.length === 0 &&
                      initialWorksheet.fileResource
                    ) ||
                    hasDuplicateGoalInEditMode
                  }
                  icon="add"
                  large
                  textKey={
                    initialWorksheet.__typename === "WorksheetTemplate" || !initialWorksheet.fileResource
                      ? "pages.documents.worksheets.saveToMyFiles"
                      : initialWorksheet.__typename === "Worksheet" && changes.length === 0
                        ? "dictionary.saved"
                        : "dictionary.save"
                  }
                  type="submit"
                />
              </div>
              {initialWorksheet.__typename === "Worksheet" && (
                <>
                  <TertiaryButton
                    icon="fileDownload"
                    large
                    onClick={e => handleDownload(e, false)}
                    textKey="models.worksheet.saveEmptyDocument"
                  />

                  <TertiaryButton
                    icon="fileDownload"
                    large
                    onClick={e => handleDownload(e, true)}
                    textKey="models.worksheet.saveCompletedDocument"
                  />
                  <TertiaryButton
                    icon="messages"
                    large
                    onClick={handleOpenMessages}
                    textKey="models.worksheet.messageExpert"
                  />
                </>
              )}
            </div>
          </div>

          <BaseErrors errors={baseErrors} />
        </div>
      </Form>
      <DeleteConfirmationAlert
        cancelKey="pages.documents.worksheets.discardChanges"
        deleteKey="pages.documents.worksheets.saveWorksheet"
        headerKey="pages.documents.worksheets.leaveWithoutSaving"
        onCancel={() => {
          unsavedChanges.opener.close();
          handleDiscard();
          setTimeout(() => history.push(unsavedChanges.leavingLocation ?? ""), 400);
        }}
        onDelete={() => onSubmit(unsavedChanges.leavingLocation ?? "")}
        opener={unsavedChanges.opener}
      />
    </div>
  );
};

export default WorksheetEditor;
